import { appRoutePath, appRoutePathPrefix, appRoutePatterns } from './appRoutePatterns';
import {
  canonicalInternalPath,
  INTERNAL_APP_ORIGIN,
  normalizeInternalAppReturnPath,
} from '@/types/internalNavigation';

export type OperationsPlanningReturnOrigin =
  | 'map'
  | 'my-forests'
  | 'forest-overview'
  | 'simulation-storage'
  | 'timber-prices';

export interface OperationsPlanningReturnTarget {
  origin: OperationsPlanningReturnOrigin;
  path: string;
}

interface OperationsPlanningHandoffInput {
  propertyId: string;
  returnTo: string;
}

function overviewPath(propertyId: string): string {
  return appRoutePath(appRoutePatterns.forestOverview, { propertyId });
}

function ownerOrigin(returnTo: string, propertyId: string): OperationsPlanningReturnOrigin | null {
  const url = new URL(returnTo, INTERNAL_APP_ORIGIN);
  if (url.pathname === appRoutePatterns.map) return 'map';
  if (url.pathname === appRoutePatterns.myForests) return 'my-forests';
  if (url.pathname === appRoutePatterns.timberPrices) return 'timber-prices';
  const standPrefix = appRoutePathPrefix(appRoutePatterns.standDetail, { propertyId });
  if (
    url.searchParams.get('manage_simulations') === '1' &&
    url.pathname.startsWith(standPrefix) &&
    /^[^/]+$/.test(url.pathname.slice(standPrefix.length))
  )
    return 'simulation-storage';
  return url.pathname === overviewPath(propertyId) ? 'forest-overview' : null;
}

/**
 * Build the property-scoped planner entry only for an owner surface that the
 * planner can name and return to safely. Unsupported origins deliberately
 * produce a direct planner URL with no history-dependent return promise.
 */
export function buildOperationsPlanningHandoff({
  propertyId,
  returnTo,
}: OperationsPlanningHandoffInput): string {
  const target = new URL(
    appRoutePath(appRoutePatterns.operationsPlanning, { propertyId }),
    INTERNAL_APP_ORIGIN,
  );
  const safeReturn = normalizeInternalAppReturnPath(returnTo);
  if (!safeReturn || !ownerOrigin(safeReturn, propertyId)) {
    return target.pathname;
  }
  target.searchParams.set('property', propertyId);
  target.searchParams.set('return_to', safeReturn);
  return canonicalInternalPath(target);
}

/** Resolve a planner return destination from query context, never history. */
export function resolveOperationsPlanningReturnTarget(
  returnTo: string | null,
  propertyId: string,
): OperationsPlanningReturnTarget {
  const fallback: OperationsPlanningReturnTarget = {
    origin: 'forest-overview',
    path: overviewPath(propertyId),
  };
  const safeReturn = normalizeInternalAppReturnPath(returnTo);
  if (!safeReturn) return fallback;
  const origin = ownerOrigin(safeReturn, propertyId);
  return origin ? { origin, path: safeReturn } : fallback;
}
